import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight, Bird, Flower2, TreePine, ScrollText, Rabbit } from "lucide-react";
import { getSymbolOfTheDay } from "@/lib/api";
import { T } from "@/context/LanguageContext";
import { SectionHeading } from "@/components/home/ExploreByState";

const SYM_ICON = { bird: Bird, flower: Flower2, tree: TreePine, motto: ScrollText, mammal: Rabbit };

/**
 * "Symbol of the Day" — a verified state symbol that rotates daily and links
 * into that state's profile. A playful, SEO-friendly hook onto the home page.
 */
export function SymbolOfTheDay() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    getSymbolOfTheDay().then((d) => { if (alive && d?.symbol) setData(d); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!data?.symbol) return null;
  const Icon = SYM_ICON[data.symbol.key] || Sparkles;

  return (
    <section className="mx-auto max-w-[1500px] px-4 py-10" data-testid="symbol-of-the-day">
      <SectionHeading kicker="Symbol of the Day" title="A verified piece of state trivia, every day" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="-mt-2 overflow-hidden rounded-2xl border border-navy/15 bg-navy text-white shadow-xl"
      >
        <Link to={`/place${data.url_path}`} className="group flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between" data-testid="symbol-of-day-link">
          <div className="flex items-center gap-4">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-oldglory-red">
              <Icon className="h-7 w-7 text-white" />
            </span>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-white/60">
                <T>{data.symbol.label}</T> · {data.name}
              </div>
              <div className="font-display text-2xl font-black leading-tight sm:text-3xl">{data.symbol.value}</div>
              {data.symbol.note && <div className="text-sm italic text-white/70">&ldquo;{data.symbol.note}&rdquo;</div>}
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 self-start rounded-full bg-white/10 px-4 py-2 text-sm font-semibold transition-colors group-hover:bg-white/20 sm:self-auto">
            <T>Explore</T> {data.name} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </span>
        </Link>
      </motion.div>
    </section>
  );
}

export default SymbolOfTheDay;
